import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Colors, Text } from '@mercator-gcp/grout';
import { capitalize } from 'lodash';
import Tooltip from 'components/elements/Tooltip';
import { FlexRow } from 'components/layout/FlexRow';
import type { ProjectWorkType, Project } from 'types';

const labelStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: Colors.TextQuiet,
};

const dotSeparatorStyle = {
  borderRadius: '50%',
  backgroundColor: Colors.TextQuiet,
  width: '4px',
  height: '4px',
};

const containerStyle = {
  width: '100%',
  justifyContent: 'flex-start',
  columnGap: 'padding_4',
};

// -8 is arbitray here. Just to give space to separator
const categoryWrapperStyle = { maxWidth: 'calc(50% - 8px)' };

const transformUncategorizedLabel = (label: string) => {
  if (label === 'Uncategorized') {
    return 'Uncategorized Work Type';
  }

  return label;
};

const RenderWithTooltip = ({ label }: { label: string }) => {
  return (
    <Tooltip tooltipText={label} placement="bottom" fullWidth>
      <Text sx={{ ...labelStyle }}>{label}</Text>
    </Tooltip>
  );
};

type Props = {
  workTypeList?: ProjectWorkType['work_type'];
  vertical?: Project['vertical'];
};

const WorkTypeAndVerticalSection = ({ vertical, workTypeList }: Props) => {
  const hasVertical = !!vertical?.length;
  const hasWorkTypes = !!workTypeList?.length;

  if (!hasVertical && !hasWorkTypes) {
    return null;
  }

  const transformedWorkTypeLabelList = useMemo(
    () =>
      workTypeList?.map((workType) => transformUncategorizedLabel(workType)).join(', ') ||
      '',
    [workTypeList],
  );

  const transformedVerticalList = useMemo(
    () =>
      vertical
        ?.split(',')
        .map((v) => capitalize(v))
        .join(', ') || '',
    [vertical],
  );

  if (hasWorkTypes && !hasVertical) {
    return <RenderWithTooltip label={transformedWorkTypeLabelList} />;
  }

  if (!hasWorkTypes && hasVertical) {
    return <RenderWithTooltip label={transformedVerticalList} />;
  }

  return (
    <FlexRow sx={containerStyle}>
      <Box sx={categoryWrapperStyle}>
        <RenderWithTooltip label={transformedWorkTypeLabelList} />
      </Box>
      <Box sx={dotSeparatorStyle} />
      <Box sx={categoryWrapperStyle}>
        <RenderWithTooltip label={transformedVerticalList} />
      </Box>
    </FlexRow>
  );
};

export default WorkTypeAndVerticalSection;
